import { authFetch, API_URL, clearAuthSession, getAuthHeaders } from "../../utils/auth";

const SETTINGS_URL = `${API_URL}/delivery/settings/`;
const ZONES_URL = `${API_URL}/delivery-zones/`;
const ESTIMATE_URL = `${API_URL}/delivery/estimate/`;

function headers() {
  return { Accept: "application/json", ...getAuthHeaders() };
}

function jsonHeaders() {
  return { Accept: "application/json", "Content-Type": "application/json", ...getAuthHeaders() };
}

function firstError(data) {
  if (!data || typeof data !== "object") return "";
  for (const [field, value] of Object.entries(data)) {
    if (Array.isArray(value) && value.length) return `${field}: ${value[0]}`;
    if (value && typeof value === "object") {
      const nested = firstError(value);
      if (nested) return `${field}: ${nested}`;
    }
    if (typeof value === "string") return `${field}: ${value}`;
  }
  return "";
}

async function parseResponse(response) {
  const contentType = response.headers.get("content-type") || "";
  const data = contentType.includes("application/json") ? await response.json() : { detail: "The request could not be completed." };
  if (response.status === 401) clearAuthSession();
  if (!response.ok) {
    const error = new Error(data?.detail || data?.message || firstError(data) || "Delivery request could not be completed.");
    error.status = response.status;
    error.data = data;
    throw error;
  }
  return data;
}

function query(params = {}) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") search.set(key, value);
  });
  const text = search.toString();
  return text ? `?${text}` : "";
}

function list(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
}

export async function fetchDeliverySettings({ signal } = {}) {
  return parseResponse(await authFetch(SETTINGS_URL, { headers: headers(), signal }));
}

export async function updateDeliverySettings(payload) {
  return parseResponse(await authFetch(SETTINGS_URL, { method: "PATCH", headers: jsonHeaders(), body: JSON.stringify(payload) }));
}

export async function fetchDeliveryZones(filters = {}) {
  const { signal, ...params } = filters;
  return list(await parseResponse(await authFetch(`${ZONES_URL}${query(params)}`, { headers: headers(), signal })));
}

export async function createDeliveryZone(payload) {
  return parseResponse(await authFetch(ZONES_URL, { method: "POST", headers: jsonHeaders(), body: JSON.stringify(payload) }));
}

export async function updateDeliveryZone(zoneId, payload) {
  return parseResponse(await authFetch(`${ZONES_URL}${zoneId}/`, { method: "PATCH", headers: jsonHeaders(), body: JSON.stringify(payload) }));
}

export async function deleteDeliveryZone(zoneId) {
  await parseResponse(await authFetch(`${ZONES_URL}${zoneId}/`, { method: "DELETE", headers: headers() }));
}

export async function estimateDelivery(estimatedTravelMinutes) {
  return parseResponse(await authFetch(ESTIMATE_URL, { method: "POST", headers: jsonHeaders(), body: JSON.stringify({ estimated_travel_minutes: Number(estimatedTravelMinutes) }) }));
}
